import {
  useState,
} from 'react';
import {
  Callout,
  Confirm,
  Field,
  FilterBar,
  Modal,
  SearchRow,
  Select,
  TableActions,
  TextInput,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  explainFailure,
  formatCount,
  formatDate,
  textArray,
  useLoad,
  SortTh,
  countryLabel,
  rowOpen,
  localDayOf,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../client.ts';
import {
  serverSort,
} from '../../screens.ts';
import { t } from '../../messages.ts';
import { useAbilities } from '../abilities.tsx';
import { affiliateMatches, type AffiliateStateFilter } from '../entity-actions.ts';
import { Failure } from '../shared.tsx';
import { AffiliateDetailModal } from './AffiliateDetailModal.tsx';
import { isAwaitingSecondPerson } from './identity-approvals.ts';
import { IdentityApprovals } from './IdentityApprovals.tsx';

function IdentityConsolidationForm({
  api,
  onDone,
  onCancel,
}: {
  api: PlatformApi;
  onDone: (note: string) => void;
  onCancel: () => void;
}) {
  const [survivor, setSurvivor] = useState('');
  const [superseded, setSuperseded] = useState('');
  const [evidence, setEvidence] = useState('');
  const [governingDeals, setGoverningDeals] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  // Batch 2 item 4 — the merge is irreversible, so a click on the form's own
  // primary opens a step that restates the two identities rather than
  // submitting straight away.
  const [confirming, setConfirming] = useState<{ governing_deals: Record<string, string> } | null>(null);

  return (
    <>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <div className="form">
        <Field label={t('aff.consolidate.survivor')}>
          <TextInput value={survivor} onChange={setSurvivor} />
        </Field>
        <Field label={t('aff.consolidate.superseded')}>
          <TextInput value={superseded} onChange={setSuperseded} />
        </Field>
        <Field label={t('aff.consolidate.evidence')}>
          <TextInput value={evidence} onChange={setEvidence} />
        </Field>
        <p className="faint">{t('aff.consolidate.willWait')}</p>
        <Field label={t('aff.consolidate.governingDeals')} hint={t('aff.consolidate.governingDealsHint')}>
          <TextInput value={governingDeals} onChange={setGoverningDeals} placeholder='{"…":"…"}' />
        </Field>
        <div className="form-actions">
          <button className="copy" disabled={busy} onClick={onCancel}>{t('common.cancel')}</button>
          <button
            className="primary"
            disabled={busy || !survivor || !superseded || !evidence}
            onClick={() => {
              setFailure(null);
              let governing_deals: Record<string, string> = {};
              try {
                governing_deals = JSON.parse(governingDeals || '{}') as Record<string, string>;
              } catch (e: unknown) {
                setFailure(e);
                return;
              }
              setConfirming({ governing_deals });
            }}
          >
            {t('aff.consolidate.submit')}
          </button>
        </div>
      </div>

      {confirming ? (
        <Confirm
          title={t('aff.consolidate.confirmTitle')}
          tone="error"
          confirmLabel={t('aff.consolidate.confirmLabel')}
          body={
            <>
              {t('aff.consolidate.mergeIntro')} <strong>{superseded.trim()}</strong>{' '}
              {t('aff.consolidate.mergeInto')} <strong>{survivor.trim()}</strong>{' '}
              {t('aff.consolidate.mergeOutro')}
            </>
          }
          onCancel={() => setConfirming(null)}
          onConfirm={async () => {
            const r = await api.consolidateIdentities({
              surviving_identity_id: survivor.trim(),
              superseded_identity_id: superseded.trim(),
              evidence: evidence.trim(),
              governing_deals: confirming.governing_deals,
            });
            onDone(isAwaitingSecondPerson(r) ? t('aff.consolidate.waiting') : t('aff.approvals.applied'));
          }}
        />
      ) : null}
    </>
  );
}

/** One row of the identity-centric list (item 8, third batch). */
type IdentityRow = Awaited<ReturnType<PlatformApi['affiliates']>>['items'][number];

export function AllAffiliates({ api, currentUserId }: { api: PlatformApi; currentUserId?: string }) {
  const { can } = useAbilities();
  const [stateFilter, setStateFilter] = useState<AffiliateStateFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [sort, setSort] = useState('operator');
  const [consolidating, setConsolidating] = useState(false);
  const [consolidationNote, setConsolidationNote] = useState<string | null>(null);
  // Item 8 — a row is a person, and a person can hold several operator
  // profiles. The row opens the popup on one of them — the operator the list
  // is filtered to, else the first — and the popup's own Operator picker
  // moves between the rest, rather than a choice standing in front of it.
  const [open, setOpen] = useState<IdentityRow | null>(null);
  const loaded = useLoad(
    () => api.affiliates(query || undefined, operatorId || undefined, sort),
    [query, operatorId, sort],
  );
  const operators = useLoad(() => api.operators(), []);
  const operatorOptions = operators.state === 'ready' ? operators.value.items : [];
  const order = serverSort(sort, setSort);
  const shown = loaded.state === 'ready'
    ? loaded.value.items.filter((a) => affiliateMatches(a, stateFilter))
    : [];

  const firstProfile = (row: IdentityRow): string =>
    (row.operators.find((o) => o.operator_id === operatorId) ?? row.operators[0])?.affiliate_id ?? '';

  return (
    <>
      <div className="page-head">
        <h1>{t('aff.list.title')}</h1>
        {can.edit ? (
          <div className="page-head-actions">
            {/* Batch 2 item 4 — an irreversible merge is not this screen's
                primary action; Confirm inside the form is what asks for
                intent now. */}
            <button className="copy" onClick={() => setConsolidating(true)}>{t('aff.list.consolidate')}</button>
          </div>
        ) : null}
      </div>

      <IdentityApprovals api={api} canEdit={can.edit} {...(currentUserId ? { currentUserId } : {})} />

      {consolidating ? (
        <Modal title={t('aff.list.consolidationTitle')} onClose={() => setConsolidating(false)}>
          <IdentityConsolidationForm
            api={api}
            onCancel={() => setConsolidating(false)}
            onDone={(note) => {
              setConsolidationNote(note);
              setConsolidating(false);
            }}
          />
        </Modal>
      ) : null}

      {open ? (
        <AffiliateDetailModal
          api={api}
          affiliateId={firstProfile(open)}
          profiles={open.operators}
          onClose={() => setOpen(null)}
        />
      ) : null}

      {/* Batch 2 review, finding 7 — the result of a consolidation just run,
          not an ambient note; read whole, not folded to two lines. */}
      {consolidationNote ? <Callout fold={false}>{consolidationNote}</Callout> : null}

      <SearchRow
        value={searchInput}
        onChange={setSearchInput}
        onSearch={() => setQuery(searchInput.trim())}
        placeholder={t('aff.list.searchPlaceholder')}
      />

      <FilterBar>
        <Field label={t('common.operator')}>
          <Select
            value={operatorId}
            onChange={setOperatorId}
            options={[
              { value: '', label: t('aff.list.everyOperator') },
              ...operatorOptions.map((o) => ({ value: o.id, label: o.display_name })),
            ]}
          />
        </Field>
        <Field label={t('common.status')}>
          <Select
            value={stateFilter}
            onChange={(v) => setStateFilter(v as AffiliateStateFilter)}
            options={[
              { value: 'all', label: t('ent.affiliates.filter.all') },
              { value: 'active', label: t('ent.affiliates.filter.active') },
              { value: 'pending', label: t('ent.affiliates.filter.pending') },
              { value: 'inactive', label: t('ent.affiliates.filter.inactive') },
              { value: 'suspended', label: t('ent.affiliates.filter.suspended') },
              { value: 'deleted', label: t('ent.affiliates.filter.deleted') },
            ]}
          />
        </Field>
      </FilterBar>

      {loaded.state === 'loading' ? <Loading rows={2} /> : null}
      {loaded.state === 'error' ? <Failure error={loaded.error} /> : null}

      {loaded.state === 'ready' ? (
        <>
          <Panel
            title={t('aff.list.panel')}
            aside={
              <div className="panel-aside">
                <span className="count count-badge">{shown.length}</span>
                <TableActions
                  title={t('aff.list.title')}
                  filename="affiliates"
                  headers={[
                    t('aff.list.colAffiliate'), t('common.email'), t('aff.list.colOperators'),
                    t('aff.list.colBrands'), t('common.country'), t('aff.list.colClicks'),
                    t('aff.list.colJoined'), t('common.status'),
                  ]}
                  keys={['affiliate', 'email', 'operators', 'brands', 'country', 'clicks', 'joined', 'status']}
                  rows={shown.map((a) => [
                    a.display_name, a.email, a.operators.map((o) => o.operator).join(' '),
                    textArray(a.brands).join(' '), a.country, String(a.clicks), localDayOf(a.joined),
                    a.identity_status !== 'ACTIVE' ? a.identity_status
                      : a.operators.some((o) => o.deleted || o.purged) ? t('ent.state.deleted') : 'ACTIVE',
                  ])}
                />
              </div>
            }
          >
            {shown.length === 0 ? (
              <Empty>{query || operatorId || stateFilter !== 'all' ? t('common.nobodyMatches') : t('aff.list.nobodyYet')}</Empty>
            ) : (
              <TableScroll>
                <table>
                  <thead>
                    <tr>
                      <SortTh sort={order} col="name">{t('aff.list.colAffiliate')}</SortTh>
                      <th>{t('common.email')}</th>
                      <SortTh sort={order} col="operator">{t('aff.list.colOperators')}</SortTh>
                      <th>{t('aff.list.colBrands')}</th>
                      <th>{t('common.country')}</th>
                      <SortTh sort={order} col="clicks" align="num">{t('aff.list.colClicks')}</SortTh>
                      <SortTh sort={order} col="joined" align="num">{t('aff.list.colJoined')}</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((a) => {
                      const brands = textArray(a.brands);
                      const openThis = () => { if (a.operators.length > 0) setOpen(a); };
                      return (
                        <tr
                          key={a.identity_id}
                          className="row-clickable"
                          onClick={openThis}
                          {...rowOpen(openThis)}
                        >
                          <td>
                            {a.display_name}
                            {/* Item 7 (third batch) — a platform-wide suspension is
                                about the person, so it belongs on the person's row. */}
                            {a.identity_status !== 'ACTIVE' ? (
                              <>{' '}<Chip tone="overdue">{a.identity_status}</Chip></>
                            ) : null}
                          </td>
                          <td className="faint">{a.email}</td>
                          <td className="faint">
                            {a.operators
                              .map((o) => `${o.operator}${o.purged ? ` ${t('ent.affiliates.inlinePurged')}` : o.deleted ? ` ${t('ent.affiliates.inlineDeleted')}` : ''}`)
                              .join(', ')}
                          </td>
                          <td className="faint">{brands.join(', ') || '—'}</td>
                          <td>{countryLabel(a.country)}</td>
                          <td className="num">{formatCount(a.clicks)}</td>
                          <td className="num faint">{formatDate(a.joined)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Panel>

          <Callout>{loaded.value.note}</Callout>
        </>
      ) : null}
    </>
  );
}
